import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiSliders } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { formatTime12hr } from '../../utils/timeFormat';
import MeetingAnalysisModal from '../MeetingAnalysisModal';
import Toast from '../Toast';
import { getMetadata } from '../../utils/encryptedStorage';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTimezone } from '../../contexts/TimezoneContext';
import AIInterviewConfigSelector from './AIInterviewConfigSelector';
import AISetupList from './AISetupList';
import InterviewScheduler from './InterviewScheduler';
import ImportCandidatesModal from './ImportCandidatesModal';
import RejectCandidateModal from './RejectCandidateModal';
import { jobFromDefaults, normalizeWorkMode, validateConfiguration, recommendedMinimum } from '../../pages/interview/config/interviewConfigurationModel';
import PriorityTopicsEditor from '../interview/PriorityTopicsEditor';
import { validateTopics } from '../../pages/ai-interview/utils/aiInterviewApi';
import { canUseCompanyFeature } from '../../utils/companyFeatureAccess';

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const candidateStage = (candidate) => String(
  candidate?.funnel_stage || candidate?.status || candidate?.candidate_status || ''
).trim().toLowerCase();

const hasEmailAndResume = (candidate) => (
  Boolean(normalizeEmail(candidate?.email || candidate?.candidate_email))
  && Boolean(candidate?.doc_id || candidate?.cv_filename || candidate?.resume_url || candidate?.resume)
);

const isReadyCandidate = (candidate) => {
  const stage = candidateStage(candidate);
  // Only PRE-interview stages belong in the Ready bucket. Candidates already scheduled/interviewed
  // are surfaced by the dedicated Invited/Booked/Completed buckets (from candidates-status), so they
  // must NOT reappear here as "ready to schedule".
  return hasEmailAndResume(candidate) && ['interested', 'shortlisted', 'interview'].includes(stage);
};

const isSetupLockedForScheduling = (setup) => Boolean(
  setup?.legacy
  || setup?.has_scheduling_session
  || Number(setup?.scheduled_count || 0) > 0
);

const splitList = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const arrayFromValue = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
  return splitList(value);
};

const inferDifficulty = (context = {}) => {
  const text = `${context.level || ''} ${context.seniority || ''} ${context.experience || ''} ${context.experience_level || ''}`.toLowerCase();
  if (text.includes('senior') || /\b([6-9]|1\d)\+?\s*(years|yrs|year)/.test(text)) return 'senior';
  if (text.includes('junior') || text.includes('fresher') || text.includes('entry') || /\b[0-1]\s*-\s*[2-3]/.test(text)) return 'junior';
  return 'mid';
};

const buildSetupDetailsFromJob = (context = {}, jobTitle = '') => {
  const skills = [
    ...arrayFromValue(context.skills),
    ...arrayFromValue(context.required_skills),
    ...arrayFromValue(context.technical_skills),
    ...arrayFromValue(context.must_have_skills),
  ].filter((skill, index, arr) => arr.findIndex((item) => item.toLowerCase() === skill.toLowerCase()) === index);
  const role = context.title || context.role || context.jobTitle || jobTitle || 'this role';
  const difficulty = inferDifficulty(context);
  return {
    role_title: role === 'this role' ? '' : role,
    role_level: context.role_level || context.level || context.seniority || difficulty,
    department: context.department || '',
    location: context.location || context.job_location || context.jobLocation || '',
    work_mode: normalizeWorkMode(context.work_mode || context.workplace_type || context.workplaceType || context.employment_mode || ''),
    objective: `Assess candidate fit for ${role}, using the JD and resume context.`,
    skills: skills.length ? skills : [role],
    question_types: ['technical', 'project experience', 'problem solving', 'behavioral'],
    difficulty,
    evaluation_criteria: ['technical accuracy', 'role relevance', 'problem solving', 'communication', 'ownership'],
    coding_or_task_required: false,
    personalization: { resume: true, jd: true },
    candidate_instructions: '',
  };
};

const aiWizardStepLabels = {
  mode: 'Interview style',
  objective: 'Objective',
  skills: 'Skills',
  difficulty: 'Difficulty',
  priority_topics: 'Priority topics',
  question_types: 'Question types',
  evaluation_criteria: 'Evaluation',
  duration: 'Duration',
  count_mode: 'Question plan',
  total_questions: 'Question count',
  own_questions: 'Your questions',
  saving: 'Saving setup',
  done: 'Ready',
};

const formatSetupCompetencies = (rows = []) => (Array.isArray(rows) ? rows : [])
  .map((item) => {
    if (!item || typeof item !== 'object') return String(item || '').trim();
    const label = item.label || item.key || '';
    return item.weight ? `${label} (${item.weight}%)` : label;
  })
  .filter(Boolean)
  .join(', ');

const formatSetupAccommodations = (acc) => {
  if (!acc || !acc.enabled) return '';
  return (Array.isArray(acc.selected_options) ? acc.selected_options : [])
    .map((item) => item?.label || item?.key)
    .filter(Boolean)
    .join(', ');
};

const formatSetupAllocation = (rows = []) => (Array.isArray(rows) ? rows : [])
  .map((row) => `${row.label || row.section}: ${row.count}`)
  .filter(Boolean)
  .join(', ');

const parseMaybeJson = (value) => {
  if (!value || typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
};

const formatTranscriptTime = (value, timezone) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const options = { hour: '2-digit', minute: '2-digit' };
    if (timezone) options.timeZone = timezone;
    return parsed.toLocaleTimeString([], options);
  }
  const match = raw.match(/(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : '';
};

const questionText = (question) => {
  if (!question) return '';
  if (typeof question === 'string') return question.trim();
  return String(question.question_text || question.text || question.question || '').trim();
};

const normalizeQuestions = (questions) => (
  Array.isArray(questions) ? questions.map(questionText).filter(Boolean) : []
);

const normalizeSpeaker = (value) => {
  const raw = String(value || '').trim();
  const lower = raw.toLowerCase();
  if (lower.includes('candidate') || lower.includes('applicant') || lower === 'user') return 'Candidate';
  if (lower.includes('interviewer') || lower.includes('assistant') || lower.includes('wiza') || lower.includes('ai')) return 'Wiza';
  return raw || 'Wiza';
};

const extractTranscriptEntries = (result, timezone) => {
  const rawTurns = parseMaybeJson(result?.turns || result?.messages || result?.transcript || result?.conversation || null);
  if (Array.isArray(rawTurns)) {
    return rawTurns.map((turn, index) => {
      if (typeof turn === 'string') {
        const match = turn.trim().match(/^\[?([^\]]*?\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:[+-]\d{2}:?\d{2}|Z)?)\]?\s*(Candidate|Interviewer|Assistant|Wiza|AI)\s*:\s*(.*)$/i)
          || turn.trim().match(/^(Candidate|Interviewer|Assistant|Wiza|AI)\s*:\s*(.*)$/i);
        if (!match) return null;
        const hasTime = match.length === 4;
        const speaker = normalizeSpeaker(hasTime ? match[2] : match[1]);
        return { speaker, role: speaker.toLowerCase(), text: (hasTime ? match[3] : match[2]).trim(), timeStr: hasTime ? formatTranscriptTime(match[1], timezone) : '', index };
      }
      if (!turn || typeof turn !== 'object') return null;
      const text = String(turn.text || turn.content || turn.message || turn.transcript || '').trim();
      if (!text) return null;
      const speaker = normalizeSpeaker(turn.role || turn.speaker || turn.author || '');
      return { speaker, role: speaker.toLowerCase(), text, timeStr: formatTranscriptTime(turn.time || turn.created_at || turn.timestamp_iso || turn.timestamp, timezone), index };
    }).filter(Boolean);
  }

  return String(result?.transcript_text || result?.transcript || '')
    .split('\n')
    .map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed || /^#{1,6}\s*(interview transcript|interview_id|session_id|candidate_id|candidate_name|role|started_at|ended_at|metadata)\b/i.test(trimmed)) return null;
      if (/^(interview_id|session_id|candidate_id|candidate_name|role|started_at|ended_at)\s*[:=]/i.test(trimmed)) return null;
      const match = trimmed.match(/^\[?([^\]]*?\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:[+-]\d{2}:?\d{2}|Z)?)\]?\s*(Candidate|Interviewer|Assistant|Wiza|AI)\s*:\s*(.*)$/i)
        || trimmed.match(/^(Candidate|Interviewer|Assistant|Wiza|AI)\s*:\s*(.*)$/i);
      if (!match) return null;
      const hasTime = match.length === 4;
      const speaker = normalizeSpeaker(hasTime ? match[2] : match[1]);
      return { speaker, role: speaker.toLowerCase(), text: (hasTime ? match[3] : match[2]).trim(), timeStr: hasTime ? formatTranscriptTime(match[1], timezone) : '', index };
    })
    .filter(Boolean);
};

const InterviewWorkspace = ({
  candidates = [],
  searchId = '',
  sourceSessionId = '',
  jobTitle = '',
  jobContext = {},
  creationOnly = false,
  onStartInterviewCreation = null,
  onOpenScorecard = null,
  onStartSchedulingAgent = null,
  onCloseCreation = null,
  externalMessage = null,
  onExternalMessageProcessed = null,
  refreshKey = 0,
  draftBlueprint = null,
  featureAccess = null,
}) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const { ianaTimezone } = useTimezone();
  const metadata = getMetadata();
  const companyId = String(metadata?.cid || '');
  const userId = metadata?.uid || metadata?.userId || metadata?.id;
  const primaryColor = settings?.theme?.colors?.primary || '#4f46e5';
  const canImportCandidates = canUseCompanyFeature(featureAccess, 'import_candidate');
  const canUseAiInterview = canUseCompanyFeature(featureAccess, 'ai_interview');
  const canUseManualInterview = canUseCompanyFeature(featureAccess, 'manual_interview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshingWorkspace, setRefreshingWorkspace] = useState(false);
  const [readyCandidates, setReadyCandidates] = useState([]);
  const [candidateStatusMap, setCandidateStatusMap] = useState({});
  const [missedCandidateMap, setMissedCandidateMap] = useState({});
  const [rescheduleRequests, setRescheduleRequests] = useState([]);
  const [calendarInterviews, setCalendarInterviews] = useState([]);
  const [interviewSetup, setInterviewSetup] = useState(null);
  const [aiInterviewSetup, setAiInterviewSetup] = useState(null);
  const [manualInterviewSetup, setManualInterviewSetup] = useState(null);
  const [selectedAiConfig, setSelectedAiConfig] = useState(null);
  // Reusable, multi-setup AI: `creatingNewSetup` forces the config editor into a fresh build (its save
  // mints a new setup identity); `setupListRefresh` re-pulls the per-setup stats after a change.
  const [creatingNewSetup, setCreatingNewSetup] = useState(false);
  const [setupListRefresh, setSetupListRefresh] = useState(0);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [activeBucket, setActiveBucket] = useState('ready');
  // Bulk-import resume-less candidates for this job (Excel). Scoped to this session only.
  const [importOpen, setImportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');   // candidate-list filter (name / email)
  const [joiningEmail, setJoiningEmail] = useState('');   // per-row "Join Meet" loading (booked manual)
  const PAGE_SIZE = 8;
  const [showSetupDetails, setShowSetupDetails] = useState(false);
  const [activeBlueprintType, setActiveBlueprintType] = useState('');
  const activeBlueprintTypeRef = useRef('');
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantMode, setAssistantMode] = useState('');
  const [assistantInput, setAssistantInput] = useState('');
  const [assistantError, setAssistantError] = useState('');
  const [setupSaving, setSetupSaving] = useState(false);
  const [resultPanel, setResultPanel] = useState({ candidate: null, loading: false, recalculating: false, result: null, analysis: null, error: '' });
  const resultRequestRef = useRef(0);
  useEffect(() => () => { resultRequestRef.current += 1; }, []);
  const [toastState, setToastState] = useState({ visible: false, message: '', type: 'error' });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [aiWizard, setAiWizard] = useState({
    step: 'mode',
    draft: null,
    saving: false,
    transcript: [],
  });

  const createAiDraft = useCallback(() => {
    const draft = jobFromDefaults(null, sourceSessionId);
    const inferredDetails = buildSetupDetailsFromJob(jobContext, jobTitle);
    return {
      ...draft,
      name: draft.name || 'AI interview setup 1',
      session_id: String(sourceSessionId || ''),
      interview_id: String(sourceSessionId || ''),
      categories: [],
      custom_questions: [],
      setup_details: {
        ...(draft.setup_details || {}),
        ...inferredDetails,
        personalization: { resume: true, jd: true, ...(inferredDetails.personalization || {}) },
      },
    };
  }, [jobContext, jobTitle, sourceSessionId]);

  const resetAiWizard = useCallback(() => {
    setAiWizard({
      step: 'mode',
      draft: createAiDraft(),
      saving: false,
      transcript: [],
    });
  }, [createAiDraft]);

  const renderQuestionPreview = (questions, { limit = 3, scroll = false } = {}) => {
    const list = normalizeQuestions(questions);
    if (!list.length) return null;
    const visible = list.slice(0, limit);
    const hiddenCount = Math.max(0, list.length - visible.length);
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 bg-white/65 p-2.5 dark:border-emerald-800 dark:bg-gray-900/35">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">Your questions</p>
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{list.length}</span>
        </div>
        <ol className={`${scroll ? 'max-h-36 overflow-y-auto pr-1' : ''} space-y-1 text-xs leading-5 text-gray-700 dark:text-gray-300`}>
          {visible.map((text, index) => (
            <li key={`${index}-${text}`} className="grid grid-cols-[1.25rem_1fr] gap-1">
              <span className="text-gray-400 dark:text-gray-500">{index + 1}.</span>
              <span className="min-w-0">{text}</span>
            </li>
          ))}
          {hiddenCount > 0 && (
            <li className="pl-5 text-gray-500 dark:text-gray-400">+ {hiddenCount} more question{hiddenCount === 1 ? '' : 's'}</li>
          )}
        </ol>
      </div>
    );
  };

  const loadWorkspace = useCallback(async () => {
    if (!sourceSessionId && !searchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Scope the calendar fetch by search_id ONLY — never cid/user_id. `/interviews/calendar/all`
      // gates on EXISTS(automation_email_sync_history WHERE company_id=cid AND user_id=uid) when those
      // are passed, which silently DROPS imported candidates: they are added under a search_id that has
      // NO email-sync history, so the gate excludes their booked interviews and the workspace loses
      // their calendar row (no Join Meet, no completion signal, no AI result id). search_id already
      // scopes this to the current job, so the ownership gate is redundant here.
      const calendarQuery = new URLSearchParams();
      if (searchId) calendarQuery.set('search_id', String(searchId));

      const setupQueryBase = companyId ? `company_id=${encodeURIComponent(companyId)}` : '';
      const setupQuery = (type) => {
        const params = new URLSearchParams(setupQueryBase);
        params.set('interview_type', type);
        return params.toString();
      };
      const requests = [
        sourceSessionId
          ? aiAutomationApi.get(`/sessions/${encodeURIComponent(sourceSessionId)}/schedulable-candidates`)
          : Promise.resolve({ candidates: [] }),
        searchId
          ? aiAutomationApi.get(`/interviews/search/${encodeURIComponent(searchId)}/candidates-status${sourceSessionId ? `?session_id=${encodeURIComponent(sourceSessionId)}` : ''}`)
          : Promise.resolve({ already_scheduled_candidates: {} }),
        aiAutomationApi.get(`/interviews/calendar/all${calendarQuery.toString() ? `?${calendarQuery.toString()}` : ''}`),
        searchId
          ? aiAutomationApi.get(`/interviews/search/${encodeURIComponent(searchId)}/setup?${setupQuery('ai')}`)
          : Promise.resolve({ exists: false }),
        searchId
          ? aiAutomationApi.get(`/interviews/search/${encodeURIComponent(searchId)}/setup?${setupQuery('manual')}`)
          : Promise.resolve({ exists: false }),
        searchId && sourceSessionId
          ? aiAutomationApi.get(`/interviews/reschedule-requests?status=pending&status=needs_manual_action&search_id=${encodeURIComponent(searchId)}&session_id=${encodeURIComponent(sourceSessionId)}${companyId ? `&cid=${encodeURIComponent(companyId)}` : ''}`)
          : Promise.resolve([]),
      ];
      const [readyResult, statusResult, calendarResult, aiSetupResult, manualSetupResult, rescheduleResult] = await Promise.allSettled(requests);
      const apiReady = readyResult.status === 'fulfilled' ? (readyResult.value?.candidates || []) : [];
      const fallbackReady = candidates.filter(isReadyCandidate);
      const mergedReady = apiReady.length > 0 ? apiReady : fallbackReady;
      setReadyCandidates(mergedReady);
      setCandidateStatusMap(statusResult.status === 'fulfilled'
        ? (statusResult.value?.already_scheduled_candidates || {})
        : {});
      setMissedCandidateMap(statusResult.status === 'fulfilled'
        ? (statusResult.value?.missed_candidates || {})
        : {});
      setCalendarInterviews(calendarResult.status === 'fulfilled'
        ? (calendarResult.value?.interviews || [])
        : []);
      const nextAiSetup = aiSetupResult.status === 'fulfilled' && aiSetupResult.value?.exists ? aiSetupResult.value : null;
      const nextManualSetup = manualSetupResult.status === 'fulfilled' && manualSetupResult.value?.exists ? manualSetupResult.value : null;
      setAiInterviewSetup(nextAiSetup);
      setManualInterviewSetup(nextManualSetup);
      setRescheduleRequests(rescheduleResult.status === 'fulfilled' && Array.isArray(rescheduleResult.value)
        ? rescheduleResult.value
        : []);
      const requestedType = activeBlueprintTypeRef.current;
      const preferredType = requestedType === 'manual' && nextManualSetup
        ? 'manual'
        : requestedType === 'ai' && nextAiSetup
          ? 'ai'
          : nextAiSetup
            ? 'ai'
            : nextManualSetup
              ? 'manual'
              : '';
      setActiveBlueprintType(preferredType);
      activeBlueprintTypeRef.current = preferredType;
      setInterviewSetup(preferredType === 'manual' ? nextManualSetup : nextAiSetup || nextManualSetup || null);
      if (nextAiSetup?.configuration) {
        setSelectedAiConfig(nextAiSetup.configuration);
      }
      if (readyResult.status === 'rejected' && statusResult.status === 'rejected' && calendarResult.status === 'rejected') {
        throw new Error('Interview workspace could not be loaded.');
      }
    } catch (loadError) {
      setError(loadError?.message || 'Failed to load interview data.');
      setRescheduleRequests([]);
    } finally {
      setLoading(false);
    }
  }, [candidates, companyId, searchId, sourceSessionId]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  // Reload when the chat flow saves/changes the setup (so the saved blueprint appears here).
  useEffect(() => {
    if (refreshKey) loadWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleRefreshWorkspace = useCallback(async () => {
    if (loading || refreshingWorkspace) return;
    setRefreshingWorkspace(true);
    try {
      await loadWorkspace();
    } finally {
      setRefreshingWorkspace(false);
    }
  }, [loadWorkspace, loading, refreshingWorkspace]);

  const openRescheduleRequests = useCallback(() => {
    const params = new URLSearchParams();
    params.append('status', 'pending');
    params.append('status', 'needs_manual_action');
    if (searchId) params.set('search_id', String(searchId));
    if (sourceSessionId) params.set('session_id', String(sourceSessionId));
    if (companyId) params.set('cid', String(companyId));
    navigate(`/reschedule-requests?${params.toString()}`);
  }, [companyId, navigate, searchId, sourceSessionId]);

  // Jump back to the first page whenever the bucket changes.
  useEffect(() => { setPage(1); }, [activeBucket, search]);

  useEffect(() => {
    if (!creationOnly) return;
    setAssistantOpen(true);
    setSelectedEmails(new Set(
      candidates
        .map((candidate) => normalizeEmail(candidate?.email || candidate?.candidate_email))
        .filter(Boolean)
    ));
  }, [candidates, creationOnly]);

  const calendarByEmail = useMemo(() => {
    const map = new Map();
    calendarInterviews.forEach((interview) => {
      const email = normalizeEmail(interview?.candidate_email);
      if (email) map.set(email, interview);
    });
    return map;
  }, [calendarInterviews]);

  const allCandidateMap = useMemo(() => {
    const map = new Map();
    [...candidates, ...readyCandidates].forEach((candidate) => {
      const email = normalizeEmail(candidate?.email || candidate?.candidate_email);
      if (email) map.set(email, { ...candidate, email, name: candidate?.name || candidate?.candidate_name || email });
    });
    Object.entries(candidateStatusMap).forEach(([email, status]) => {
      const key = normalizeEmail(email);
      map.set(key, { ...(map.get(key) || {}), ...status, email: key, name: status?.name || map.get(key)?.name || key });
    });
    Object.entries(missedCandidateMap).forEach(([email, missed]) => {
      const key = normalizeEmail(email);
      map.set(key, {
        ...(map.get(key) || {}),
        missed_interview: true,
        missed_reason: missed?.missed_reason || 'Not attended the interview',
        missed_scheduled_date: missed?.scheduled_date || null,
        missed_scheduled_time: missed?.scheduled_time || null,
        email: key,
        name: missed?.name || map.get(key)?.name || key,
      });
    });
    return map;
  }, [candidateStatusMap, candidates, missedCandidateMap, readyCandidates]);

  const buckets = useMemo(() => {
    const scheduledEmails = new Set(Object.keys(candidateStatusMap).map(normalizeEmail));
    const ready = readyCandidates
      .map((candidate) => allCandidateMap.get(normalizeEmail(candidate?.email || candidate?.candidate_email)))
      .filter(Boolean)
      .filter((candidate) => !scheduledEmails.has(normalizeEmail(candidate.email)));
    const scheduled = [];
    const booked = [];
    const completed = [];
    Object.entries(candidateStatusMap).forEach(([email, status]) => {
      const candidate = allCandidateMap.get(normalizeEmail(email)) || status;
      const calendar = calendarByEmail.get(normalizeEmail(email));
      const row = { ...candidate, ...status, calendar };
      // Completion: manual interviews signal via calendar (note-taker/meeting completed, or the slot's
      // scheduled time having passed → calendar.status==='completed'), AI interviews via the backend
      // `completed` flag (interview_status/ai_status) since they have no calendar signal. NOTE: the
      // calendar endpoint returns status as 'completed' | 'upcoming' — never 'interviewed' — so a
      // past manual interview must be matched on 'completed' (the old 'interviewed' check was dead,
      // which left completed manual interviews stuck in Booked showing "Join Meet" forever).
      const calStatus = String(calendar?.status || '').toLowerCase();
      const isAiRow = String(calendar?.interview_type || '').toLowerCase() === 'ai'
        || String(status?.interview_type || '').toLowerCase() === 'ai'
        || aiInterviewSetup?.interview_type === 'ai';
      const isCompleted = Boolean(status?.completed)
        || Boolean(calendar?.meeting_completed)
        || calStatus === 'interviewed'
        // Manual only: once the slot's scheduled time has passed the meeting is over, so it leaves
        // Booked (no more "Join Meet") and moves to Completed. AI keeps relying on its own completion
        // flags above, so a booked-but-not-yet-taken AI interview is never marked done by time alone.
        || (!isAiRow && calStatus === 'completed');
      if (isCompleted) completed.push(row);
      else if (status?.booking_status === 'booked') booked.push(row);
      else scheduled.push(row);
    });
    return { ready, scheduled, booked, completed };
  }, [aiInterviewSetup, allCandidateMap, calendarByEmail, candidateStatusMap, readyCandidates]);

  const bucketOptions = [
    { id: 'ready', label: 'Ready', count: buckets.ready.length },
    { id: 'scheduled', label: 'Invited', count: buckets.scheduled.length },
    { id: 'booked', label: 'Booked', count: buckets.booked.length },
    { id: 'completed', label: 'Completed', count: buckets.completed.length },
  ];
  const visibleCandidates = buckets[activeBucket] || [];

  // Join a booked MANUAL interview from the workspace — mirrors the candidate-detail "Join Meet":
  // trigger the AI Note Taker (best-effort), then open the Google Meet link. Opening always happens so
  // a note-taker hiccup never blocks the recruiter from joining.
  const handleJoinMeet = useCallback(async (candidate) => {
    const cal = candidate?.calendar || {};
    const meetLink = cal.google_meet_link;
    if (!meetLink) return;
    const email = normalizeEmail(candidate.email || candidate.candidate_email);
    window.open(meetLink, '_blank', 'noopener,noreferrer');
    setJoiningEmail(email);
    try {
      if (cal.id) {
        await aiAutomationApi.post(`/interviews/${encodeURIComponent(String(cal.id))}/join-meeting`, {});
      }
    } catch (err) {
      console.warn('[InterviewWorkspace] join-meeting trigger failed; Meet was already opened:', err);
    } finally {
      setJoiningEmail('');
    }
  }, []);
  // Client-side search over the active bucket (matches name or email).
  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visibleCandidates;
    return visibleCandidates.filter((c) => {
      const name = String(c.name || c.candidate_name || '').toLowerCase();
      const email = String(c.email || c.candidate_email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [visibleCandidates, search]);
  // Defensive: a candidate can only be scheduled if it has an email (the booking link / invite target).
  const selectedCandidates = buckets.ready.filter(
    (candidate) => selectedEmails.has(normalizeEmail(candidate.email)) && Boolean(normalizeEmail(candidate.email)),
  );

  const toggleCandidate = (email) => {
    const key = normalizeEmail(email);
    setSelectedEmails((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Show the "saved blueprint" card for an AI setup (it carries a real config) always; for a MANUAL
  // setup only once it has actually been used (a candidate invited/booked/completed) — otherwise a
  // manual setup that was created-then-abandoned would linger as a stale blueprint.
  const scheduledCount = buckets.scheduled.length + buckets.booked.length + buckets.completed.length;
  const enabledAiInterviewSetup = canUseAiInterview ? aiInterviewSetup : null;
  const enabledManualInterviewSetup = canUseManualInterview ? manualInterviewSetup : null;
  const enabledInterviewSetup = (
    (interviewSetup?.interview_type === 'ai' && canUseAiInterview)
    || (interviewSetup?.interview_type === 'manual' && canUseManualInterview)
  ) ? interviewSetup : null;
  const savedBlueprintSetup = enabledAiInterviewSetup || (enabledManualInterviewSetup && scheduledCount > 0 ? enabledManualInterviewSetup : null);
  const availableBlueprints = [
    enabledAiInterviewSetup ? { type: 'ai', label: 'AI setup', setup: enabledAiInterviewSetup } : null,
    enabledManualInterviewSetup && scheduledCount > 0 ? { type: 'manual', label: 'Manual setup', setup: enabledManualInterviewSetup } : null,
  ].filter(Boolean);
  const selectedBlueprint = availableBlueprints.find((item) => item.type === activeBlueprintType)?.setup
    || savedBlueprintSetup;
  const showSavedBlueprint = Boolean(selectedBlueprint);

  // Only candidates with an email can be scheduled — select-all/unselect-all targets just those.
  const selectableReady = buckets.ready.filter((candidate) => Boolean(normalizeEmail(candidate.email)));
  const allReadySelected = selectableReady.length > 0
    && selectableReady.every((candidate) => selectedEmails.has(normalizeEmail(candidate.email)));
  const toggleSelectAll = () => {
    setSelectedEmails(() => (allReadySelected
      ? new Set()
      : new Set(selectableReady.map((candidate) => normalizeEmail(candidate.email)))));
  };

  // Paginate the visible bucket so a long list stays usable.
  const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedCandidates = filteredCandidates.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Kick off conversational creation with the type preselected from the panel buttons.
  const startCreate = (choice) => {
    if (selectedCandidates.length === 0) return;
    if (choice === 'ai' && !canUseAiInterview) return;
    if (choice === 'manual' && !canUseManualInterview) return;
    const setupForChoice = choice === 'ai' ? aiInterviewSetup : choice === 'manual' ? manualInterviewSetup : interviewSetup;
    if (typeof onStartInterviewCreation === 'function') {
      onStartInterviewCreation({
        candidates: selectedCandidates, searchId, sourceSessionId, jobTitle, interviewSetup: setupForChoice,
        setupChoice: choice,
      });
    }
  };

  const openScheduler = () => {
    if (selectedCandidates.length === 0) return;
    const onlyEnabledChoice = canUseAiInterview && !canUseManualInterview ? 'ai' : canUseManualInterview && !canUseAiInterview ? 'manual' : '';
    if (!creationOnly && typeof onStartInterviewCreation === 'function') {
      onStartInterviewCreation({
        candidates: selectedCandidates,
        searchId,
        sourceSessionId,
        jobTitle,
        interviewSetup: selectedBlueprint || enabledInterviewSetup,
        ...(onlyEnabledChoice ? { setupChoice: onlyEnabledChoice } : {}),
      });
      return;
    }
    setAssistantOpen(true);
    setAssistantError('');
  };

  const createJobSetup = async (mode, config = null, { forceNew = false } = {}) => {
    if (mode === 'ai' && !canUseAiInterview) return null;
    if (mode === 'manual' && !canUseManualInterview) return null;
    if (!searchId) {
      setAssistantError('Search context is missing. Please reopen this job and try again.');
      return null;
    }
    if (!companyId) {
      setAssistantError('Company context is missing. Please sign in again.');
      return null;
    }
    setSetupSaving(true);
    setAssistantError('');
    try {
      const data = await aiAutomationApi.post(`/interviews/search/${encodeURIComponent(searchId)}/setup`, {
        company_id: String(companyId),
        session_id: String(sourceSessionId || ''),
        interview_type: mode,
        setup_name: mode === 'ai' ? (config?.name || config?.setup_name || '') : '',
        ai_configuration_id: config?.id || null,
        setup_snapshot: config || {},
        // force_new binds this freshly-minted config to a NEW durable setup sibling rather than
        // re-resolving (and leaving the new config orphaned on) the job's existing active AI setup.
        force_new: Boolean(forceNew),
      });
      setInterviewSetup(data);
      if (data?.interview_type === 'ai') setAiInterviewSetup(data);
      if (data?.interview_type === 'manual') setManualInterviewSetup(data);
      setAssistantMode(data?.interview_type || mode);
      if (config?.id) setSelectedAiConfig(config);
      setSetupListRefresh((n) => n + 1);   // stats/lock may have changed
      return data;
    } catch (err) {
      setAssistantError(err?.response?.data?.detail || err?.detail || err.message || 'Failed to create interview setup.');
      return null;
    } finally {
      setSetupSaving(false);
    }
  };

  // "Create New Setup" — drop the current selection so the editor builds a fresh, durable sibling
  // (its save uses force_new, minting a new immutable config identity rather than editing an existing one).
  const startNewAiSetup = () => {
    if (!canUseAiInterview) return;
    setSelectedAiConfig(null);
    setCreatingNewSetup(true);
    setAssistantMode('ai');
    setAssistantOpen(true);
    setAssistantError('');
  };

  // "Use this setup" — bind the workspace to a specific existing AI setup (loads its config so scheduling
  // threads that setup's id/config). Locked setups load read-only (invite-more only).
  const useExistingSetup = async (setup) => {
    if (!canUseAiInterview) return;
    if (!setup?.setup_id) return;
    setCreatingNewSetup(false);
    setAssistantMode('ai');
    setAssistantError('');
    let config = null;
    if (setup.ai_configuration_id) {
      try {
        const data = await aiAutomationApi.get(`/ai-interview/config/${encodeURIComponent(setup.ai_configuration_id)}`);
        config = data?.configuration || null;
      } catch (err) {
        setAssistantError(err?.response?.data?.detail || err?.detail || err.message || 'Failed to load the selected setup.');
      }
    }
    setSelectedAiConfig(config);
    const nextSetup = {
      exists: true,
      interview_type: 'ai',
      setup_id: setup.setup_id,
      round_id: setup.round_id,
      ai_configuration_id: setup.ai_configuration_id,
      setup_name: setup.setup_name || '',
      display_name: setup.display_name || setup.name || setup.setup_name || '',
      configuration: config,
      locked: Boolean(setup.locked),
      scheduled_count: Number(setup?.stats?.invited || 0),
    };
    setAiInterviewSetup(nextSetup);
    setInterviewSetup(nextSetup);
  };

  const handOffToSchedulingAgent = (mode, setup = interviewSetup, config = selectedAiConfig) => {
    if (mode === 'ai' && !canUseAiInterview) return false;
    if (mode === 'manual' && !canUseManualInterview) return false;
    if (typeof onStartSchedulingAgent !== 'function') return false;
    onStartSchedulingAgent({
      interviewType: mode,
      searchId,
      sourceSessionId,
      jobTitle,
      candidates: selectedCandidates.length > 0 ? selectedCandidates : candidates,
      setup,
      aiConfiguration: config || setup?.configuration || null,
    });
    return true;
  };

  const chooseManual = async () => {
    if (!canUseManualInterview) return;
    setAssistantMode('manual');
    if (creationOnly && handOffToSchedulingAgent('manual', null, null)) {
      if (onCloseCreation) onCloseCreation();
      return;
    }
    const setup = manualInterviewSetup || await createJobSetup('manual');
    if (setup) setSchedulerOpen(true);
  };

  const chooseAi = () => {
    if (!canUseAiInterview) return;
    setAssistantMode('ai');
    setAssistantOpen(true);
    setAssistantError('');
    if (creationOnly) resetAiWizard();
  };

  const continueAiScheduling = async () => {
    if (!canUseAiInterview) return;
    const activeAiSetup = enabledAiInterviewSetup || (enabledInterviewSetup?.interview_type === 'ai' ? enabledInterviewSetup : null);
    const config = selectedAiConfig || activeAiSetup?.configuration;
    if (!config?.id && !activeAiSetup?.ai_configuration_id) {
      setAssistantError('Save the AI interview setup before creating slots.');
      return;
    }
    if (creationOnly && handOffToSchedulingAgent('ai', null, config)) {
      if (onCloseCreation) onCloseCreation();
      return;
    }
    const setup = activeAiSetup
      ? activeAiSetup
      : await createJobSetup('ai', config);
    if (setup) setSchedulerOpen(true);
  };

  const saveAiWizardConfig = async (draft) => {
    const cfg = {
      ...draft,
      name: draft.name || 'AI interview setup 1',
      session_id: String(sourceSessionId || ''),
      interview_id: String(draft.interview_id || sourceSessionId || ''),
      scoring_config: {},
      threshold_config: {},
    };
    const errors = validateConfiguration(cfg, 'job');
    if (errors.length) {
      setAssistantError(errors[0]);
      return null;
    }
    setAiWizard((prev) => ({ ...prev, step: 'saving', saving: true }));
    setAssistantError('');
    try {
      const data = await aiAutomationApi.post(`/ai-interview/config?company_id=${encodeURIComponent(companyId)}`, cfg);
      const configuration = data?.configuration || cfg;
      setSelectedAiConfig(configuration);
      if (!creationOnly) {
        await createJobSetup('ai', configuration);
      }
      setAiWizard((prev) => ({
        ...prev,
        step: 'done',
        draft: configuration,
        saving: false,
        transcript: [...prev.transcript, { from: 'assistant', text: 'AI interview setup is saved. You can continue to availability when ready.' }],
      }));
      return configuration;
    } catch (err) {
      setAiWizard((prev) => ({ ...prev, saving: false }));
      setAssistantError(err?.response?.data?.detail || err?.detail || err.message || 'Failed to save AI interview setup.');
      return null;
    }
  };

  const updateAiDraft = (updater) => {
    const current = aiWizard.draft || createAiDraft();
    const nextDraft = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
    setAiWizard((prev) => ({ ...prev, draft: nextDraft }));
    return nextDraft;
  };

  const handleAiWizardText = async (rawText) => {
    const input = String(rawText || '').trim();
    const text = input.toLowerCase();
    if (!input) return;
    let shouldSave = false;
    let saveDraft = null;

    setAssistantError('');
    setAiWizard((prev) => ({ ...prev, transcript: [...prev.transcript, { from: 'user', text: input }] }));

    if (!aiWizard.draft) {
      resetAiWizard();
      return;
    }

    const currentDraft = aiWizard.draft;
    const addSkillMatch = input.match(/^add\s+(?:the\s+)?skill\s+(.+)$/i) || input.match(/^include\s+(?:the\s+)?skill\s+(.+)$/i);
    if (addSkillMatch) {
      const additions = splitList(addSkillMatch[1]);
      if (additions.length) {
        updateAiDraft((draft) => {
          const existing = draft.setup_details?.skills || [];
          const skills = [...existing, ...additions].filter((skill, index, arr) => (
            arr.findIndex((item) => String(item).toLowerCase() === String(skill).toLowerCase()) === index
          ));
          return { ...draft, setup_details: { ...(draft.setup_details || {}), skills } };
        });
        return;
      }
    }

    if (aiWizard.step === 'mode') {
      let mode = '';
      if (text.includes('hybrid')) mode = 'hybrid';
      else if (text.includes('own') || text.includes('your') || text.includes('recruiter') || text.includes('fixed questions')) mode = 'recruiter';
      else if (text.includes('ai') || text.includes('fully') || text.includes('auto')) mode = 'ai';
      if (!mode) {
        setAssistantError('Choose Fully AI, Your questions only, or Hybrid.');
        return;
      }
      updateAiDraft((draft) => ({
        ...draft,
        mode,
        question_count_mode: mode === 'recruiter' ? 'fixed' : draft.question_count_mode,
        custom_questions: [],
      }));
      // Priority topics apply only to AI/hybrid (recruiter-only asks the recruiter's own questions).
      setAiWizard((prev) => ({ ...prev, step: mode === 'recruiter' ? 'duration' : 'priority_topics' }));
      return;
    }

    if (aiWizard.step === 'priority_topics') {
      // The editor writes topics into the draft directly; chat text just advances (or adds typed topics).
      const skip = ['skip', 'none', 'no', 'continue', 'next', 'done'].some((word) => text === word);
      if (!skip && input) {
        const added = splitList(input);
        if (added.length) {
          updateAiDraft((draft) => {
            const existing = draft.setup_details?.priority_topics || [];
            const merged = [...existing, ...added].filter((t, i, arr) => (
              arr.findIndex((x) => String(x).toLowerCase() === String(t).toLowerCase()) === i));
            return { ...draft, setup_details: { ...(draft.setup_details || {}), priority_topics: merged } };
          });
          return;
        }
      }
      setAiWizard((prev) => ({ ...prev, step: 'duration' }));
      return;
    }

    if (aiWizard.step === 'duration') {
      const useDefault = ['default', 'same', 'ok', 'okay', 'yes', 'use default', 'keep'].some((word) => text.includes(word));
      const duration = useDefault ? Number(currentDraft.duration_minutes || 20) : Number(input.match(/\d+/)?.[0] || 0);
      if (!duration || duration < 15) {
        setAssistantError('Enter a duration of at least 15 minutes, or type “use default”.');
        return;
      }
      const nextDraft = updateAiDraft((draft) => ({ ...draft, duration_minutes: duration }));
      if (nextDraft.mode === 'recruiter') setAiWizard((prev) => ({ ...prev, step: 'total_questions' }));
      else setAiWizard((prev) => ({ ...prev, step: 'count_mode' }));
      return;
    }

    if (aiWizard.step === 'count_mode') {
      const unrestricted = text.includes('handle') || text.includes('coverage') || text.includes('unrestricted') || text.includes('let ai') || text.includes('auto') || text.includes('decide');
      const fixed = text.includes('fixed') || text.includes('count') || /\d+/.test(text);
      if (!unrestricted && !fixed) {
        setAssistantError('Choose “AI handles coverage” for coverage-first interviews, or “fixed count” only for a legacy setup.');
        return;
      }
      const nextDraft = updateAiDraft((draft) => ({ ...draft, question_count_mode: unrestricted ? 'unrestricted' : 'fixed' }));
      if (unrestricted) {
        if (nextDraft.mode === 'hybrid') setAiWizard((prev) => ({ ...prev, step: 'own_questions' }));
        else {
          shouldSave = true;
          saveDraft = nextDraft;
        }
      } else {
        setAiWizard((prev) => ({ ...prev, step: 'total_questions' }));
      }
    }

    if (aiWizard.step === 'total_questions') {
      const total = Number(input.match(/\d+/)?.[0] || 0);
      if (!total || total < 1) {
        setAssistantError('Enter a question count, for example 8.');
        return;
      }
      const nextDraft = updateAiDraft((draft) => ({ ...draft, total_questions: total, question_count_mode: 'fixed' }));
      if (nextDraft.mode === 'recruiter' || nextDraft.mode === 'hybrid') setAiWizard((prev) => ({ ...prev, step: 'own_questions' }));
      else {
        shouldSave = true;
        saveDraft = nextDraft;
      }
    }

    if (aiWizard.step === 'own_questions') {
      if (['done', 'finish', 'finished', 'save'].includes(text)) {
        const questions = currentDraft.custom_questions || [];
        if (currentDraft.mode === 'hybrid' && questions.length < 1) {
          setAssistantError('Add at least one must-ask question before finishing hybrid setup.');
          return;
        }
        if (currentDraft.mode === 'recruiter' && questions.length !== Number(currentDraft.total_questions || 0)) {
          setAssistantError(`Add exactly ${currentDraft.total_questions} question(s). You have ${questions.length}.`);
          return;
        }
        shouldSave = true;
        saveDraft = currentDraft;
      } else {
        const nextDraft = updateAiDraft((draft) => ({
          ...draft,
          custom_questions: [
            ...(draft.custom_questions || []),
            { question_text: input, category: 'technical', expected_skills: draft.setup_details?.skills || [], weightage: 1 },
          ],
        }));
        const questionCount = nextDraft.custom_questions.length;
        if (nextDraft.mode === 'recruiter' && questionCount >= Number(nextDraft.total_questions || 0)) {
          shouldSave = true;
          saveDraft = nextDraft;
        }
      }
    }

    if (shouldSave && saveDraft) {
      await saveAiWizardConfig(saveDraft);
    }
  };

  const processAssistantText = (rawText) => {
    const text = String(rawText || '').trim().toLowerCase();
    if (!text) return;
    if (!assistantMode) {
      if (text.includes('manual') || text.includes('human') || text.includes('interviewer')) {
        if (canUseManualInterview) chooseManual();
        else if (canUseAiInterview) chooseAi();
        else setAssistantError('Interview access is not enabled for this company.');
        return;
      }
      if (text.includes('ai') || text.includes('wiza') || text.includes('automated')) {
        if (canUseAiInterview) chooseAi();
        else if (canUseManualInterview) chooseManual();
        else setAssistantError('Interview access is not enabled for this company.');
        return;
      }
      if (canUseAiInterview && canUseManualInterview) {
        setAssistantError('Please choose AI Interview or Manual Interview, or type one of those options.');
      } else if (canUseAiInterview) {
        chooseAi();
      } else if (canUseManualInterview) {
        chooseManual();
      } else {
        setAssistantError('Interview access is not enabled for this company.');
      }
      return;
    }
    if (assistantMode === 'ai') {
      if (creationOnly) {
        handleAiWizardText(rawText);
        return;
      }
      setAssistantError('Use the AI setup card below to create the interview blueprint. The free-text draft option inside that card can turn your notes into setup fields.');
      return;
    }
    setAssistantError('Manual interview is ready. Continue to availability to create or reuse slots.');
  };

  const handleAssistantMessage = () => {
    const text = assistantInput.trim();
    if (!text) return;
    setAssistantInput('');
    processAssistantText(text);
  };

  useEffect(() => {
    if (!externalMessage?.token) return;
    processAssistantText(externalMessage.text);
    if (typeof onExternalMessageProcessed === 'function') {
      onExternalMessageProcessed(externalMessage.token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMessage?.token]);

  // Open the result for a COMPLETED interview. AI interviews resolve to the scored `/ai-result`;
  // MANUAL interviews resolve to the AI Note Taker's meeting analysis (mirrors the chat card modal),
  // so both faces of the interview report are viewable from the workspace — not only AI ones.
  const openResultPanel = async (candidate, refresh = false) => {
    const generation = ++resultRequestRef.current;
    const updateResult = (updater) => {
      if (resultRequestRef.current === generation) setResultPanel(updater);
    };
    const interviewId = candidate?.calendar?.id || candidate?.interview_id || candidate?.id;
    if (!interviewId) {
      setResultPanel({ candidate, loading: false, recalculating: false, result: null, analysis: null, error: 'Scheduled interview id is missing for this candidate.' });
      return;
    }
    const isAi = String(candidate?.calendar?.interview_type || '').toLowerCase() === 'ai'
      || String(candidate?.interview_type || '').toLowerCase() === 'ai'
      || Boolean(candidate?.calendar?.ai_launch_token || candidate?.calendar?.ai_interview_id);
    if (isAi) {
      setResultPanel({ candidate, scheduledInterviewId: interviewId, loading: false, result: null, analysis: null, error: '' });
      return;
    }
    setResultPanel((prev) => ({
      candidate,
      loading: !refresh && !(prev.candidate && prev.result),
      recalculating: refresh || prev.recalculating || false,
      result: refresh ? prev.result : null,
      analysis: refresh ? prev.analysis : null,
      transcript: refresh ? prev.transcript : '',
      manualInterviewId: refresh ? prev.manualInterviewId : null,
      error: '',
    }));
    try {
      // Manual: a few manual rows are still AI-scored — try the scored result first; if it carries real
      // content, show it. Otherwise load the note-taker meeting analysis (generate it when absent).
      try {
        const aiData = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(interviewId))}/ai-result`);
        if (!aiData) throw new Error('Could not retrieve this interview result.');
        if (aiData && (aiData.evaluation || aiData.transcript_text || aiData.transcript || aiData.metadata)) {
          updateResult({ candidate, scheduledInterviewId: interviewId, loading: false, result: aiData, analysis: null, error: '' });
          return;
        }
      } catch (error) {
        if (error?.status !== 400) throw error;
      }
      const notes = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(interviewId))}/notes`);
      const transcript = notes?.notes?.transcript || '';
      let analysis = notes?.meeting_analysis_json || null;
      if ((refresh || !analysis || analysis.success === false) && notes?.interview_id) {
        try { analysis = await aiAutomationApi.post(`/interviews/${encodeURIComponent(String(interviewId))}/notes/analyze`); } catch { /* keep any cached analysis */ }
      }
      if (analysis && analysis.success !== false) {
        updateResult({ candidate, loading: false, recalculating: false, result: null, analysis, transcript, manualInterviewId: interviewId, error: '' });
      } else if (transcript || notes?.ai_note_taker_meeting_id) {
        updateResult({ candidate, loading: false, recalculating: false, result: null, analysis: null, transcript, manualInterviewId: interviewId, error: '' });
      } else {
        updateResult({ candidate, loading: false, recalculating: false, result: null, analysis: null, error: 'No meeting analysis is available for this interview yet.' });
      }
    } catch (err) {
      updateResult((prev) => ({
        candidate,
        loading: false,
        recalculating: false,
        result: refresh ? prev.result : null,
        analysis: refresh ? prev.analysis : null,
        transcript: refresh ? prev.transcript : '',
        manualInterviewId: refresh ? prev.manualInterviewId : null,
        error: err?.response?.data?.detail || err?.detail || err.message || 'Could not load the interview result.',
      }));
    }
  };

  const renderResultPanel = () => {
    if (!resultPanel.candidate) return null;
    return (
      <MeetingAnalysisModal
        open
        title="Interview result"
        onClose={() => {
          resultRequestRef.current += 1;
          setResultPanel({ candidate: null, loading: false, recalculating: false, result: null, analysis: null, error: '' });
        }}
        candidate={resultPanel.candidate}
        scheduledInterviewId={resultPanel.scheduledInterviewId}
        aiResult={resultPanel.result}
        analysis={resultPanel.analysis}
        transcript={resultPanel.transcript}
        manualInterviewId={resultPanel.manualInterviewId}
        loading={resultPanel.loading}
        error={resultPanel.error}
        onRecalculate={(basis) => openResultPanel(resultPanel.candidate, true, basis)}
        recalculating={resultPanel.recalculating}
        onReject={() => setRejectTarget(resultPanel.candidate)}
      />
    );
    // eslint-disable-next-line no-unreachable
    const result = resultPanel.result || {};
    const evaluation = result.evaluation || {};
    const metadata = result.metadata || {};
    const coverage = evaluation.coverage || {};
    const score = Number(evaluation.overall_score);
    const maxScore = Number(evaluation.overall_max || 100);
    const hasScore = evaluation.available && Number.isFinite(score);
    const transcriptEntries = extractTranscriptEntries(result, ianaTimezone);
    const decision = String(evaluation.decision || '').toLowerCase();
    const tone = decision === 'select' ? 'emerald' : decision === 'reject' ? 'rose' : 'amber';
    const toneClasses = {
      emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200',
      rose: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200',
      amber: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200',
    };
    const closeResult = () => setResultPanel({ candidate: null, loading: false, result: null, error: '' });
    return createPortal(
      <div
        className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-gray-950/60 p-4 backdrop-blur-sm sm:items-center"
        onClick={closeResult}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
          onClick={(event) => event.stopPropagation()}
        >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">AI interview result</p>
            <h3 className="mt-1 text-lg font-semibold text-gray-950 dark:text-white">{resultPanel.candidate.name || resultPanel.candidate.email}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{resultPanel.candidate.email}</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => openResultPanel(resultPanel.candidate, true)} disabled={resultPanel.loading} className="rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/20">Recalculate</button>
            <button type="button" onClick={closeResult} className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">Close</button>
          </div>
        </div>

        {resultPanel.loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading AI interview result...</div>
        ) : resultPanel.error ? (
          <div className="p-6"><div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">{resultPanel.error}</div></div>
        ) : (
          <div className="space-y-5 p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
              <div className={`rounded-xl border p-5 ${toneClasses[tone]}`}>
                <p className="text-xs font-semibold uppercase tracking-wide opacity-80">Recommendation</p>
                <h4 className="mt-1 text-2xl font-bold">{hasScore ? (evaluation.decision_label || 'Evaluated') : 'Review required'}</h4>
                <p className="mt-2 text-sm leading-6">{hasScore ? (evaluation.summary || 'AI scoring completed.') : (evaluation.error || 'Score is not available yet.')}</p>
              </div>
              <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Score</p>
                <p className="mt-3 text-4xl font-bold text-gray-950 dark:text-white">{hasScore ? score : '--'}<span className="text-base font-semibold text-gray-400"> / {maxScore || 100}</span></p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"><p className="text-xs font-semibold uppercase text-gray-500">Questions asked</p><p className="mt-1 text-lg font-semibold text-gray-950 dark:text-white">{metadata.questions_asked ?? coverage.main_questions_asked ?? 0}</p></div>
              <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"><p className="text-xs font-semibold uppercase text-gray-500">Coverage</p><p className="mt-1 text-lg font-semibold text-gray-950 dark:text-white">{coverage.required_skills?.length ? `${coverage.covered_skills?.length || 0}/${coverage.required_skills.length}` : 'Available'}</p></div>
              <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"><p className="text-xs font-semibold uppercase text-gray-500">Ended by</p><p className="mt-1 text-lg font-semibold capitalize text-gray-950 dark:text-white">{String(metadata.end_reason || 'completed').replaceAll('_', ' ')}</p></div>
            </div>

            {Array.isArray(evaluation.criteria) && evaluation.criteria.length > 0 && (
              <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-950 dark:text-white">Scoring breakdown</h4>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {evaluation.criteria.map((criterion) => (
                    <div key={criterion.key || criterion.label} className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <div className="flex justify-between gap-3 text-sm"><span className="font-semibold text-gray-900 dark:text-white">{criterion.label}</span><span className="font-semibold text-gray-600 dark:text-gray-200">{criterion.score}/{criterion.max}</span></div>
                      {criterion.reason && <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">{criterion.reason}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-gray-950 dark:text-white">Conversational transcript</h4>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">{transcriptEntries.length} messages</span>
              </div>
              <div className="mt-4 max-h-[460px] space-y-3 overflow-y-auto rounded-xl bg-gray-50 p-4 dark:bg-gray-950/40">
                {transcriptEntries.length === 0 ? (
                  <p className="text-sm text-gray-500">No transcript messages available.</p>
                ) : transcriptEntries.map((entry, index) => {
                  const isCandidate = entry.role.includes('candidate');
                  return (
                    <div key={`${entry.index || index}-${entry.text.slice(0, 16)}`} className={`flex ${isCandidate ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm ${isCandidate ? 'rounded-br-sm bg-indigo-600 text-white' : 'rounded-bl-sm border border-gray-200 bg-white text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'}`}>
                        <div className={`mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide ${isCandidate ? 'text-indigo-100' : 'text-gray-400'}`}>
                          <span>{entry.speaker}</span>{entry.timeStr && <span>{entry.timeStr}</span>}
                        </div>
                        <p>{entry.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>,
      document.body,
    );
  };

  const renderChoiceButton = (label, value, description = '') => (
    <button
      key={label}
      type="button"
      onClick={() => handleAiWizardText(value || label)}
      className="rounded-xl border border-gray-200 bg-white p-3 text-left transition hover:border-indigo-400 hover:bg-indigo-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-indigo-500 dark:hover:bg-indigo-950/30"
    >
      <span className="block text-sm font-semibold text-gray-950 dark:text-white">{label}</span>
      {description && <span className="mt-1 block text-xs leading-5 text-gray-500 dark:text-gray-400">{description}</span>}
    </button>
  );

  const getAiWizardPrompt = () => {
    const draft = aiWizard.draft || createAiDraft();
    const ownCount = draft.custom_questions?.length || 0;
    const total = Number(draft.total_questions || 0);
    if (aiWizard.step === 'mode') {
      return {
        title: 'How should Wiza run this AI interview?',
        body: 'I will use the JD and candidate resume for skills, level, categories and evaluation focus. Choose who controls the questions.',
        choices: [
          ['Fully AI generated', 'fully ai', 'Wiza plans and asks all questions.'],
          ['Your questions only', 'your questions only', 'Wiza asks exactly the questions you provide.'],
          ['Hybrid', 'hybrid', 'Your must-ask questions plus Wiza coverage.'],
        ],
      };
    }
    if (aiWizard.step === 'priority_topics') {
      return {
        title: 'Any priority topics for Wiza to focus on?',
        body: 'Add your own topics if Wiza should ask only those topics in the technical part. Leave this empty to use the JD skills.',
      };
    }
    if (aiWizard.step === 'duration') {
      const topics = draft.setup_details?.priority_topics || [];
      const skills = draft.setup_details?.skills || [];
      const customCount = draft.mode === 'hybrid' ? (draft.custom_questions?.length || 0) : 0;
      const cover = (topics.length || skills.length) + customCount;
      const rec = cover ? recommendedMinimum(cover) : null;
      const basis = [
        customCount ? `your ${customCount} question(s)` : '',
        topics.length ? `${topics.length} topic(s)` : skills.length ? `${skills.length} skill(s)` : '',
      ].filter(Boolean).join(' plus ');
      const advisory = rec
        ? ` For ${basis || 'this setup'} plus the usual intro, resume, problem-solving and behavioural questions, that's about ${rec.questions} main questions (plus follow-ups) ≈ ${rec.minutes} min. You can still choose any duration from 15 minutes upward.`
        : '';
      return {
        title: 'How long should this AI interview be?',
        body: `Default is ${draft.duration_minutes || 20} minutes. Reply with a number, or type “use default”.${advisory}`,
        choices: [
          [`Use ${draft.duration_minutes || 20} min`, 'use default'],
          ['30 min', '30'],
          ['45 min', '45'],
        ],
      };
    }
    if (aiWizard.step === 'count_mode') {
      return {
        title: 'How should Wiza cover the interview?',
        body: 'The JD topics are already loaded. Use coverage-first mode so duration creates a minimum baseline and Wiza continues only for unresolved required evidence while time remains.',
        choices: [
          ['AI handles coverage', 'AI handles coverage', 'Use duration and role coverage instead of a strict count.'],
          ['Fixed count (legacy)', 'fixed count', 'Use only for old fixed-count setups.'],
        ],
      };
    }
    if (aiWizard.step === 'total_questions') return { title: 'How many questions?', body: `Default is ${draft.total_questions || 10}. Reply with a number. For your-questions-only, this is how many questions you will provide.` };
    if (aiWizard.step === 'own_questions') {
      return {
        title: draft.mode === 'hybrid' ? 'Add your must-ask questions' : `Add question ${ownCount + 1}${total ? ` of ${total}` : ''}`,
        body: draft.mode === 'hybrid'
          ? `Reply with one question at a time. You have added ${ownCount}. Type “done” when finished.`
          : `Reply with one question. I will save once you add ${total} question${total === 1 ? '' : 's'}.`,
      };
    }
    if (aiWizard.step === 'saving') return { title: 'Saving AI interview setup...', body: 'One moment while I create the job-specific AI setup.' };
    return { title: 'AI setup is ready', body: 'The interview blueprint is saved. Continue to availability when you are ready.' };
  };

  const renderAiWizard = () => {
    const draft = aiWizard.draft || createAiDraft();
    const prompt = getAiWizardPrompt();
    const detail = draft.setup_details || {};
    const summaryItems = [
      draft.mode ? ['Style', draft.mode === 'ai' ? 'Fully AI' : draft.mode === 'recruiter' ? 'Your questions only' : 'Hybrid'] : null,
      detail.role_title ? ['Role', detail.role_title] : null,
      detail.role_level ? ['Level', detail.role_level] : null,
      detail.department ? ['Department', detail.department] : null,
      detail.location ? ['Location', detail.location] : null,
      detail.work_mode ? ['Work mode', detail.work_mode] : null,
      detail.skills?.length ? ['Skills', detail.skills.join(', ')] : null,
      detail.difficulty ? ['Difficulty', detail.difficulty] : null,
      draft.duration_minutes ? ['Duration', `${draft.duration_minutes} min`] : null,
      draft.question_count_mode ? ['Plan', draft.question_count_mode === 'unrestricted' ? 'AI handles coverage' : `${draft.total_questions || 'Fixed'} questions`] : null,
      draft.custom_questions?.length ? ['Your questions', `${draft.custom_questions.length}`] : null,
    ].filter(Boolean);

    return (
      <div className="space-y-4">
        {aiWizard.transcript.length > 0 && (
          <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Replies captured</p>
            <div className="space-y-2">
              {aiWizard.transcript.slice(-5).map((turn, index) => (
                <div key={`${index}-${turn.text}`} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-indigo-600 px-3 py-2 text-sm leading-5 text-white">
                    {turn.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4 dark:border-indigo-800 dark:bg-indigo-950/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">{aiWizardStepLabels[aiWizard.step] || 'AI setup'}</p>
              <h4 className="mt-1 text-base font-semibold text-gray-950 dark:text-white">{prompt.title}</h4>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-gray-900 dark:text-indigo-300">
              {aiWizard.step === 'done' ? 'Ready' : 'Reply in chat'}
            </span>
          </div>
          <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-300">{prompt.body}</p>
          {prompt.choices && (
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {prompt.choices.map(([label, value, description]) => renderChoiceButton(label, value, description))}
            </div>
          )}
        </div>

        {summaryItems.length > 0 && (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {summaryItems.map(([label, value]) => (
              <div key={label} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
                <p className="mt-1 text-xs leading-5 text-gray-800 dark:text-gray-200">{value}</p>
              </div>
            ))}
          </div>
        )}

        {aiWizard.step === 'priority_topics' && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <PriorityTopicsEditor
              topics={draft.setup_details?.priority_topics || []}
              onChange={(next) => updateAiDraft((d) => ({ ...d, setup_details: { ...(d.setup_details || {}), priority_topics: next } }))}
              onValidate={(topics) => validateTopics(companyId, { topics, sessionId: String(sourceSessionId || '') })}
              example={(draft.setup_details?.skills || []).slice(0, 3).join(', ')}
            />
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => setAiWizard((prev) => ({ ...prev, step: 'duration' }))}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {draft.custom_questions?.length > 0 && aiWizard.step === 'own_questions' && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Questions added</p>
            <ol className="mt-2 space-y-2 text-sm text-gray-700 dark:text-gray-300">
              {draft.custom_questions.map((question, index) => <li key={`${index}-${question.question_text}`}>{index + 1}. {question.question_text}</li>)}
            </ol>
          </div>
        )}

        {aiWizard.step === 'done' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
            <div>
              <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">AI interview setup saved</p>
              <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-300">Next step is slot creation and candidate booking invitations.</p>
            </div>
            <button type="button" onClick={continueAiScheduling} disabled={setupSaving} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              Continue to availability
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderAssistant = () => {
    if (!assistantOpen) return null;
    const hasSelection = selectedCandidates.length > 0;
    const activeAiSetup = enabledAiInterviewSetup || (enabledInterviewSetup?.interview_type === 'ai' ? enabledInterviewSetup : null);
    const activeManualSetup = enabledManualInterviewSetup || (enabledInterviewSetup?.interview_type === 'manual' ? enabledInterviewSetup : null);
    const setupForMode = assistantMode === 'ai' ? activeAiSetup : assistantMode === 'manual' ? activeManualSetup : null;
    const savedType = setupForMode && isSetupLockedForScheduling(setupForMode) ? setupForMode.interview_type : '';
    const aiReady = Boolean((selectedAiConfig || activeAiSetup?.configuration)?.id || activeAiSetup?.ai_configuration_id);
    return (
      <div className="mt-5 overflow-hidden rounded-xl border border-indigo-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-cyan-50 px-5 py-4 dark:border-gray-700 dark:from-indigo-950/40 dark:to-cyan-950/30">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">Interview assistant</p>
          <h3 className="mt-1 text-lg font-semibold text-gray-950 dark:text-white">Schedule interview for {jobTitle || 'this job'}</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {hasSelection
              ? `${selectedCandidates.length} candidate${selectedCandidates.length !== 1 ? 's' : ''} selected. I will reuse the saved job setup once it exists.`
              : 'Select ready candidates first, then continue the scheduling flow here.'}
          </p>
        </div>

        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-4 p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">W</div>
              <div className="max-w-2xl rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 text-sm leading-6 text-gray-800 dark:bg-gray-800 dark:text-gray-100">
                {savedType
                  ? `This job already uses ${savedType === 'ai' ? 'AI Interview' : 'Manual Interview'}. Future scheduling will follow that setup.`
                  : canUseAiInterview && canUseManualInterview
                    ? 'Choose how this interview should run. Manual uses the existing interviewer/calendar flow. AI creates an interview blueprint first, then candidates pick a slot from the same scheduling flow.'
                    : canUseAiInterview
                      ? 'AI Interview is enabled for this company, so I will continue with the AI interview setup.'
                      : canUseManualInterview
                        ? 'Manual Interview is enabled for this company, so I will continue with the interviewer and calendar flow.'
                        : 'Interview access is not enabled for this company.'}
              </div>
            </div>

            {!savedType && !assistantMode && (
              <div className="grid gap-3 sm:grid-cols-2">
                {canUseAiInterview && (
                  <button type="button" onClick={chooseAi} className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-left transition hover:border-indigo-400 dark:border-indigo-800 dark:bg-indigo-950/30">
                    <p className="text-sm font-semibold text-gray-950 dark:text-white">AI Interview</p>
                    <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">Create a job-specific AI setup, then reuse slots and invitation flow without selecting a human interviewer.</p>
                  </button>
                )}
                {canUseManualInterview && (
                  <button type="button" onClick={chooseManual} disabled={setupSaving} className="rounded-xl border border-gray-200 bg-white p-4 text-left transition hover:border-indigo-300 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-sm font-semibold text-gray-950 dark:text-white">Manual Interview</p>
                    <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">Use the current interviewer, Google Calendar, slot reuse, and candidate booking flow.</p>
                  </button>
                )}
              </div>
            )}

            {canUseAiInterview && (assistantMode === 'ai' || savedType === 'ai') && (
              <div className="space-y-4">
                {creationOnly ? (
                  renderAiWizard()
                ) : (
                  <>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/70">
                      <p className="text-sm font-semibold text-gray-950 dark:text-white">AI setup rules</p>
                      <div className="mt-3 grid gap-2 text-xs leading-5 text-gray-600 dark:text-gray-300 sm:grid-cols-3">
                        <div className="rounded-lg bg-white p-3 dark:bg-gray-900">Fully AI: duration creates a minimum baseline, then AI continues only for missing required coverage.</div>
                        <div className="rounded-lg bg-white p-3 dark:bg-gray-900">Your questions only: fixed question count is required and every question must be entered.</div>
                        <div className="rounded-lg bg-white p-3 dark:bg-gray-900">Hybrid: recruiter questions stay fixed; AI baseline and repair questions cover unresolved role evidence.</div>
                      </div>
                    </div>
                    <AISetupList
                      searchId={searchId}
                      companyId={companyId}
                      isDark={isDark}
                      refreshKey={setupListRefresh}
                      activeSetupId={activeAiSetup?.setup_id || null}
                      onCreateNew={startNewAiSetup}
                      onUseSetup={useExistingSetup}
                    />
                    <AIInterviewConfigSelector
                      companyId={companyId}
                      sourceSessionId={sourceSessionId}
                      value={creatingNewSetup ? null : (selectedAiConfig || activeAiSetup?.configuration || null)}
                      forceNew={creatingNewSetup}
                      locked={!creatingNewSetup && (Boolean(activeAiSetup?.locked) || isSetupLockedForScheduling(activeAiSetup))}
                      onRequestNew={startNewAiSetup}
                      onChange={(config) => {
                        const wasCreatingNew = creatingNewSetup;
                        setSelectedAiConfig(config);
                        setCreatingNewSetup(false);
                        if (config?.id) {
                          // A freshly-created config → bind it to a durable setup sibling (force_new);
                          // an existing config re-resolves its setup normally.
                          createJobSetup('ai', config, { forceNew: wasCreatingNew });
                        }
                      }}
                      isDark={isDark}
                      primaryColor={primaryColor}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                      <div>
                        <p className="text-sm font-semibold text-gray-950 dark:text-white">Ready for slot creation?</p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This opens the existing availability screen with the saved AI setup and selected candidates.</p>
                      </div>
                      <button type="button" onClick={continueAiScheduling} disabled={!hasSelection || !aiReady || setupSaving} className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
                        {setupSaving ? 'Saving setup...' : 'Continue to availability'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {canUseManualInterview && (assistantMode === 'manual' || savedType === 'manual') && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/70">
                <p className="text-sm font-semibold text-gray-950 dark:text-white">Manual interview setup is ready</p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Next, pick or reuse interviewer slots and send invitations. This keeps the current manual scheduling flow unchanged.</p>
                <button
                  type="button"
                  onClick={() => {
	                    if (creationOnly && handOffToSchedulingAgent('manual', activeManualSetup, null)) {
                      if (onCloseCreation) onCloseCreation();
                      return;
                    }
                    setSchedulerOpen(true);
                  }}
                  disabled={!hasSelection}
                  className="mt-4 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Continue to availability
                </button>
              </div>
            )}

            {assistantError && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">{assistantError}</div>}

            {creationOnly ? (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900 dark:border-indigo-800 dark:bg-indigo-950/30 dark:text-indigo-200">
                Reply using the main chat input below. {canUseAiInterview && canUseManualInterview ? 'For example, type “AI interview” or “manual interview”.' : canUseAiInterview ? 'AI interview is enabled for this company.' : canUseManualInterview ? 'Manual interview is enabled for this company.' : 'Interview access is not enabled for this company.'}
              </div>
            ) : (
              <div className="flex gap-2 rounded-xl border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
                <input
                  value={assistantInput}
                  onChange={(event) => setAssistantInput(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') handleAssistantMessage(); }}
                  placeholder={canUseAiInterview && canUseManualInterview ? 'Type “AI interview”, “manual interview”, or describe what is missing...' : canUseAiInterview ? 'Type “AI interview” or describe what is missing...' : canUseManualInterview ? 'Type “manual interview” or describe what is missing...' : 'Interview access is not enabled'}
                  className="min-w-0 flex-1 rounded-lg border-0 bg-transparent px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
                />
                <button type="button" onClick={handleAssistantMessage} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white dark:bg-white dark:text-gray-900">Send</button>
              </div>
            )}
          </div>

          <aside className="border-t border-gray-100 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-950/40 lg:border-l lg:border-t-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Selected candidates</p>
            <div className="mt-3 space-y-2">
              {selectedCandidates.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No candidates selected.</p>
              ) : selectedCandidates.map((candidate) => {
                const email = normalizeEmail(candidate.email || candidate.candidate_email);
                return (
                  <div key={email} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{candidate.name || email}</p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">{email}</p>
                  </div>
                );
              })}
            </div>
          </aside>
        </div>
      </div>
    );
  };

  if (creationOnly) {
    return (
      <div className="mx-auto w-full max-w-5xl p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Interview creation</p>
            <h2 className="text-lg font-semibold text-gray-950 dark:text-white">{jobTitle || 'Schedule interview'}</h2>
          </div>
          {onCloseCreation && (
            <button
              type="button"
              onClick={onCloseCreation}
              className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Close
            </button>
          )}
        </div>

        {renderAssistant()}
      </div>
    );
  }

  return (
    <div className={creationOnly ? 'w-full p-3 sm:p-4' : 'w-full'}>
      {/* "Interview pipeline" heading removed — the Schedule action now lives as an action button
          in the candidate list box below, and the freed space lets more candidates show. */}
      {draftBlueprint && draftBlueprint.active && (() => {
        const sd = draftBlueprint.setup_details || {};
        const skills = Array.isArray(sd.skills) ? sd.skills : [];
        const qtypes = Array.isArray(sd.question_types) ? sd.question_types : [];
        const modeLabel = { ai: 'Fully AI', recruiter: 'Your questions only', hybrid: 'Hybrid' }[draftBlueprint.mode] || '—';
        const row = (label, value) => value ? (
          <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">{label}:</span> {value}</p>
        ) : null;
        return (
	          <div className="mb-3 overflow-hidden rounded-xl border border-sky-300 bg-gradient-to-br from-sky-50 to-indigo-50 shadow-sm dark:border-sky-800 dark:from-sky-900/20 dark:to-indigo-900/10">
            <div className="flex items-center justify-between gap-2 border-b border-sky-200/70 bg-white/50 px-4 py-2.5 dark:border-sky-800/70 dark:bg-gray-900/30">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Wiza AI</span>
                <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">Building blueprint…</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${draftBlueprint.complete ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-800/50 dark:text-emerald-200' : 'bg-amber-100 text-amber-700 dark:bg-amber-800/50 dark:text-amber-200'}`}>
                {draftBlueprint.complete ? 'Ready to save' : 'In progress'}
              </span>
            </div>
            <div className="p-4">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{draftBlueprint.name || 'AI interview setup'}</p>
            <div className="mt-2 space-y-1">
              {row('Control type', draftBlueprint.mode ? modeLabel : null)}
              {row('Role', sd.role_title)}
              {row('Level', sd.role_level)}
              {row('Department', sd.department)}
              {row('Location', sd.location)}
              {row('Work mode', sd.work_mode)}
              {row('Objective', sd.objective)}
              {(draftBlueprint.mode === 'ai' || draftBlueprint.mode === 'hybrid') && Array.isArray(sd.priority_topics) && sd.priority_topics.length
                ? row('Priority topics', sd.priority_topics.join(', ')) : null}
              {row('Skills', skills.join(', '))}
              {row('Difficulty', sd.difficulty)}
              {row('Duration', draftBlueprint.duration_minutes ? `${draftBlueprint.duration_minutes} min` : null)}
              {draftBlueprint.question_count_mode === 'unrestricted'
                ? row('Questions', 'Cover all requirements')
                : row('Questions', draftBlueprint.total_questions)}
              {(draftBlueprint.mode === 'recruiter' || draftBlueprint.mode === 'hybrid') && row('Your questions', `${draftBlueprint.custom_questions_count || 0} added`)}
              {(draftBlueprint.mode === 'recruiter' || draftBlueprint.mode === 'hybrid') && renderQuestionPreview(draftBlueprint.custom_questions, { limit: 3 })}
              {draftBlueprint.mode === 'hybrid' && row('Placement', draftBlueprint.placement_strategy)}
              {row('Modes', Array.isArray(sd.interview_modes) && sd.interview_modes.length ? sd.interview_modes.join(', ') : null)}
              {row('Competencies', formatSetupCompetencies(sd.required_competencies))}
              {row('Mandatory criteria', Array.isArray(sd.mandatory_criteria) && sd.mandatory_criteria.length ? sd.mandatory_criteria.join(', ') : null)}
              {row('Disqualifiers', Array.isArray(sd.disqualifying_criteria) && sd.disqualifying_criteria.length ? sd.disqualifying_criteria.join(', ') : null)}
              {row('Question types', Array.isArray(sd.preferred_question_types) && sd.preferred_question_types.length ? sd.preferred_question_types.join(', ') : (qtypes.length ? qtypes.join(', ') : (draftBlueprint.mode === 'ai' || draftBlueprint.mode === 'hybrid' ? 'AI decides' : null)))}
              {row('Accommodations', formatSetupAccommodations(sd.interview_accommodations))}
              {row('Allocation', formatSetupAllocation(sd.question_allocation))}
              {row('Practical task', (sd.practical_task_required || sd.coding_or_task_required) ? `${sd.practical_task?.title || 'Yes'}${sd.practical_task_minutes ? ` (${sd.practical_task_minutes} min)` : ''}` : null)}
            </div>
            <p className="mt-2 text-xs text-sky-700 dark:text-sky-300">Keep answering in the chat — this setup updates as you go. Company defaults apply unless this setup overrides them.</p>
            </div>
          </div>
        );
      })()}

      {loading && !draftBlueprint && !showSavedBlueprint ? (
        <div className="animate-pulse rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900" aria-busy="true" aria-label="Loading interview setup">
          <div className="flex items-center gap-2">
            <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-28 rounded bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="mt-3 h-3 w-3/4 rounded bg-gray-100 dark:bg-gray-800" />
          <div className="mt-2 h-3 w-1/2 rounded bg-gray-100 dark:bg-gray-800" />
          <div className="mt-4 flex gap-2">
            <div className="h-8 w-28 rounded-lg bg-gray-100 dark:bg-gray-800" />
            <div className="h-8 w-28 rounded-lg bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
      ) : showSavedBlueprint ? (() => {
        const setup = selectedBlueprint;
        return (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/90 p-2.5 dark:border-emerald-800 dark:bg-emerald-900/20 sm:p-3">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {setup.interview_type === 'ai' && (
                    <span className="inline-flex items-center rounded-md bg-indigo-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Wiza AI</span>
                  )}
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Saved interview blueprint</p>
                </div>
                {availableBlueprints.length > 1 && (
                  <div className="mt-1.5 inline-grid max-w-full grid-cols-2 overflow-hidden rounded-md border border-emerald-300 bg-white text-[10px] font-semibold dark:border-emerald-700 dark:bg-gray-900">
                    {availableBlueprints.map((item) => {
                      const active = item.type === activeBlueprintType;
                      return (
                        <button
                          key={item.type}
                          type="button"
                          aria-pressed={active}
                          onClick={() => {
                            setActiveBlueprintType(item.type);
                            activeBlueprintTypeRef.current = item.type;
                            setInterviewSetup(item.setup);
                            setShowSetupDetails(false);
                          }}
                          className={`min-w-0 truncate px-2.5 py-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${active ? 'bg-emerald-600 text-white shadow-sm dark:bg-emerald-500 dark:text-gray-950' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'}`}
                          title={item.label}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                )}
	            <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
	              {setup.interview_type === 'ai'
	                ? (setup.display_name || setup.setup_name || setup.configuration?.name || 'Interview with Wiza (AI)')
                : (setup.display_name || setup.setup_name || 'Manual interview · Round 1')}
            </p>
            {setup.interview_type === 'ai' && (() => {
              const cfg = setup.configuration || {};
              const sd = cfg.setup_details || {};
              const skills = Array.isArray(sd.skills) ? sd.skills : [];
              const modes = Array.isArray(sd.interview_modes) ? sd.interview_modes.filter(Boolean) : [];
              const competencies = Array.isArray(sd.required_competencies) ? sd.required_competencies : [];
              const mandatory = Array.isArray(sd.mandatory_criteria) ? sd.mandatory_criteria : [];
              const disqualifiers = Array.isArray(sd.disqualifying_criteria) ? sd.disqualifying_criteria : [];
              const allocation = Array.isArray(sd.question_allocation) ? sd.question_allocation : [];
              const chip = (value, tone = 'emerald') => value ? (
                <span
                  key={value}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    tone === 'gray'
                      ? 'bg-white/80 text-gray-700 ring-1 ring-gray-200 dark:bg-gray-900/60 dark:text-gray-200 dark:ring-gray-700'
                      : 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800'
                  }`}
                >
                  {value}
                </span>
              ) : null;
              const summaryBits = [
                sd.role_title,
                sd.role_level,
                sd.location,
                sd.work_mode,
                cfg.duration_minutes ? `${cfg.duration_minutes} min` : null,
                cfg.question_count_mode === 'unrestricted' ? 'cover all requirements' : (cfg.total_questions ? `${cfg.total_questions} questions` : null),
              ].filter(Boolean);
              const allocationLabel = formatSetupAllocation(allocation);
              return (
                <div className="mt-1.5 space-y-1.5">
                  <p className="text-xs leading-5 text-gray-700 dark:text-gray-300">
                    {summaryBits.length ? summaryBits.join(' · ') : 'Saved AI setup ready for scheduling.'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {modes.slice(0, 4).map((mode) => chip(mode))}
                    {competencies.length ? chip(`${competencies.length} competencies`, 'gray') : null}
                    {mandatory.length ? chip(`${mandatory.length} mandatory`, 'gray') : null}
                    {disqualifiers.length ? chip(`${disqualifiers.length} disqualifiers`, 'gray') : null}
                    {skills.length ? chip(`${skills.length} skills`, 'gray') : null}
                    {(cfg.threshold_config?.select || cfg.threshold_config?.maybe) ? chip(`threshold ${cfg.threshold_config.select ?? 70}/${cfg.threshold_config.maybe ?? 50}`, 'gray') : null}
                  </div>
                  {allocationLabel && (
                    <p className="line-clamp-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                      Allocation: {allocationLabel}
                    </p>
                  )}
                </div>
              );
            })()}
                {setup.interview_type === 'manual' && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">Type:</span> Manual interview</p>
                    {Number(setup.duration_minutes || 0) > 0 && <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">Duration:</span> {Number(setup.duration_minutes)} min</p>}
                    <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">Scheduled:</span> {Number(setup.scheduled_count || 0)} session{Number(setup.scheduled_count || 0) === 1 ? '' : 's'}</p>
                    {setup.interviewer_id && <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">Interviewer ID:</span> {setup.interviewer_id}</p>}
                <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">Link type:</span> Google Meet</p>
              </div>
            )}
            {showSetupDetails && setup.interview_type === 'ai' && (() => {
              const cfg = setup.configuration || {};
              const sd = cfg.setup_details || {};
              const person = sd.personalization || {};
              const modeLabel = { ai: 'Fully AI', recruiter: 'Your questions only', hybrid: 'Hybrid' }[cfg.mode] || cfg.mode;
              const customCount = Array.isArray(cfg.custom_questions) ? cfg.custom_questions.length : 0;
              const row = (label, value) => value ? (
                <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">{label}:</span> {value}</p>
              ) : null;
              return (
                <div className="mt-3 space-y-1 border-t border-emerald-200 pt-3 dark:border-emerald-800">
	                  <AISetupList
                    searchId={searchId}
                    companyId={companyId}
                    isDark={isDark}
                    refreshKey={setupListRefresh}
	                    activeSetupId={setup?.setup_id || null}
                    onUseSetup={useExistingSetup}
                    compact
                  />
                  {row('Control type', modeLabel)}
                  {row('Modes', Array.isArray(sd.interview_modes) && sd.interview_modes.length ? sd.interview_modes.join(', ') : null)}
                  {row('Question types', ((sd.preferred_question_types || sd.question_types) || []).join(', '))}
                  {row('Competencies', formatSetupCompetencies(sd.required_competencies))}
                  {row('Mandatory criteria', Array.isArray(sd.mandatory_criteria) && sd.mandatory_criteria.length ? sd.mandatory_criteria.join(', ') : null)}
                  {row('Disqualifiers', Array.isArray(sd.disqualifying_criteria) && sd.disqualifying_criteria.length ? sd.disqualifying_criteria.join(', ') : null)}
                  {row('Thresholds', cfg.threshold_config?.select || cfg.threshold_config?.maybe ? `Recommend >= ${cfg.threshold_config.select ?? 70}, Review >= ${cfg.threshold_config.maybe ?? 50}` : null)}
                  {row('Accommodations', formatSetupAccommodations(sd.interview_accommodations))}
                  {row('Allocation', formatSetupAllocation(sd.question_allocation))}
                  {row('Practical task', (sd.practical_task_required || sd.coding_or_task_required) ? `${sd.practical_task?.title || 'Yes'}${sd.practical_task_minutes ? ` (${sd.practical_task_minutes} min)` : ''}` : null)}
                  {cfg.question_count_mode === 'unrestricted' ? row('Questions', 'Cover all requirements') : row('Questions', cfg.total_questions)}
                  {(cfg.mode === 'recruiter' || cfg.mode === 'hybrid') && row('Your questions', `${customCount} provided`)}
                  {(cfg.mode === 'recruiter' || cfg.mode === 'hybrid') && renderQuestionPreview(cfg.custom_questions, { limit: 8, scroll: true })}
                  {cfg.mode === 'hybrid' && row('Placement', cfg.placement_strategy)}
                  {row('Follow-ups', cfg.followup_mode)}
                  {row('Personalize on', [person.resume ? 'Resume' : null, person.jd ? 'JD' : null].filter(Boolean).join(' + '))}
                </div>
              );
            })()}
                {showSetupDetails && (
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                  {canUseAiInterview && canUseManualInterview
                    ? 'Future scheduling can reuse this setup, or you can choose the other interview type from Schedule.'
                    : 'Future scheduling can reuse this setup.'}
                </p>
                )}
              </div>
              <div className="flex w-full shrink-0 flex-wrap items-center gap-2 sm:w-auto lg:justify-end">
                <button
                  type="button"
                  onClick={handleRefreshWorkspace}
                  disabled={refreshingWorkspace || loading}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-700 dark:bg-gray-900 dark:text-emerald-200 dark:hover:bg-gray-800 sm:flex-none"
                >
                  <svg viewBox="0 0 24 24" className={`h-3.5 w-3.5 ${refreshingWorkspace ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 2v6h-6" /><path d="M3 22v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.13-3.36L21 8" /><path d="M20.49 15a9 9 0 0 1-14.13 3.36L3 16" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  {refreshingWorkspace ? 'Refreshing...' : 'Refresh'}
                </button>
                {setup.interview_type === 'ai' && onOpenScorecard && (
                  <button type="button" onClick={() => onOpenScorecard(setup.setup_id, sourceSessionId)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 dark:border-emerald-700 dark:bg-gray-900 dark:text-emerald-200 sm:flex-none">
                    <FiSliders aria-hidden="true" /> Scorecard
                  </button>
                )}
                {setup.interview_type === 'ai' && (
                  <button type="button" onClick={() => setShowSetupDetails((v) => !v)} className="flex-1 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 dark:border-emerald-700 dark:bg-gray-900 dark:text-emerald-200 sm:flex-none">
                    {showSetupDetails ? 'Hide details' : 'Review setup'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })() : !draftBlueprint && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Create the interview for this job</p>
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {selectedCandidates.length} selected
            </span>
          </div>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {selectedCandidates.length === 0
              ? 'Select at least one ready candidate below, then pick how to interview them.'
              : 'Set it up once — every candidate you invite uses the same interview.'}
          </p>
          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            {canUseAiInterview && (
              <button
                type="button"
                onClick={() => startCreate('ai')}
                disabled={selectedCandidates.length === 0}
                className="group flex flex-col rounded-xl border border-indigo-200 bg-indigo-50/40 p-3.5 text-left transition hover:border-indigo-400 hover:bg-indigo-50 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-800/60 dark:bg-indigo-900/15 dark:hover:bg-indigo-900/25"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm6.5 10l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2zM5.5 13l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z"/></svg>
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">Interview with Wiza</p>
                      <span className="rounded bg-indigo-600 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-white">AI</span>
                    </div>
                    <p className="text-[11px] font-medium text-indigo-600 dark:text-indigo-300">Recommended</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">AI runs the whole interview and scores every candidate. Build it in a quick chat.</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                  Set up with Wiza
                  <svg viewBox="0 0 24 24" className="h-3 w-3 transition group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </button>
            )}
            {canUseManualInterview && (
              <button
                type="button"
                onClick={() => startCreate('manual')}
                disabled={selectedCandidates.length === 0}
                className="group flex flex-col rounded-xl border border-gray-200 bg-white p-3.5 text-left transition hover:border-gray-400 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-500"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5 0-9 2.5-9 6v1h18v-1c0-3.5-4-6-9-6z"/></svg>
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white">Manual interview</p>
                    <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Human interviewer</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">Schedule a human interviewer with calendar invites and Meet links.</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Set up manually
                  <svg viewBox="0 0 24 24" className="h-3 w-3 transition group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Columns key off the PANEL's own width (auto-fit), not the viewport — this panel is a resizable
          aside, so a viewport `lg:` breakpoint would cram 4 cards into a 290px panel. auto-fit gives a
          clean 2×2 when narrow and up to 4-across when wide. Each card stacks label-over-number and lets
          the label truncate, so nothing overflows or "moves outer" at any width or digit count. */}
      <div className="mt-3 grid gap-2 grid-cols-[repeat(auto-fit,minmax(120px,1fr))]">
        {bucketOptions.map((bucket) => (
          <button key={bucket.id} type="button" onClick={() => setActiveBucket(bucket.id)}
            className={`flex min-w-0 flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-left transition ${activeBucket === bucket.id ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900'}`}>
            <p className="w-full truncate text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{bucket.label}</p>
            <p className="text-lg font-bold leading-none tabular-nums text-gray-950 dark:text-white">{bucket.count}</p>
          </button>
        ))}
      </div>

      {renderAssistant()}

      <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        {canImportCandidates && (
          <ImportCandidatesModal
            open={importOpen}
            onClose={() => setImportOpen(false)}
            sessionId={sourceSessionId}
            searchId={searchId}
            companyId={companyId}
            onImported={() => loadWorkspace()}
          />
        )}
        {/* Row 1 — title + primary actions (do-something buttons) */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{bucketOptions.find((item) => item.id === activeBucket)?.label} candidates</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {activeBucket === 'ready'
                ? (selectedCandidates.length > 0 ? `${selectedCandidates.length} selected of ${selectableReady.length}` : 'Select candidates to schedule.')
                : 'Current interview state for this job.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
          {activeBucket === 'ready' && (
            <>
              {rescheduleRequests.length > 0 && (
                <button
                  type="button"
                  onClick={openRescheduleRequests}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 dark:hover:bg-amber-900/30"
                >
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Reschedule requests ({rescheduleRequests.length})
                </button>
              )}
              {showSavedBlueprint && (
                <button type="button" onClick={openScheduler} disabled={selectedCandidates.length === 0}
                  className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-45">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" strokeLinecap="round" /></svg>
                  Schedule{selectedCandidates.length > 0 ? ` (${selectedCandidates.length})` : ''}
                </button>
              )}
              {canImportCandidates && (
                <button type="button" onClick={() => setImportOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300 dark:hover:bg-emerald-900/30">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M9.5 12.5l1.8 2 2.2-3.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  import candidate
                </button>
              )}
            </>
          )}
          </div>
        </div>
        {/* Row 2 — find / select toolbar */}
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
          <div className="relative min-w-0 flex-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" strokeLinecap="round" /></svg>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-9 pr-8 text-sm text-gray-900 placeholder-gray-400 transition focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500 dark:focus:bg-gray-900"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} aria-label="Clear search"
                className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 transition hover:text-gray-600 dark:hover:text-gray-200">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" /></svg>
              </button>
            )}
          </div>
          {activeBucket === 'ready' && selectableReady.length > 0 && (
            <button type="button" onClick={toggleSelectAll}
              className="shrink-0 rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-900/20">
              {allReadySelected ? 'Unselect all' : 'Select all'}
            </button>
          )}
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading interview pipeline...</div>
        ) : error ? (
          <div className="p-8 text-center"><p className="text-sm text-red-600">{error}</p><button type="button" onClick={loadWorkspace} className="mt-3 text-xs font-semibold text-indigo-600">Retry</button></div>
        ) : filteredCandidates.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">
            {search.trim() ? `No candidates match “${search.trim()}”.` : 'No candidates in this bucket.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {pagedCandidates.map((candidate) => {
              const email = normalizeEmail(candidate.email || candidate.candidate_email);
              const selected = selectedEmails.has(email);
              return (
                <div key={email} className={`flex items-center gap-3 px-4 py-3 transition ${selected ? 'bg-indigo-50/60 dark:bg-indigo-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}>
                  {activeBucket === 'ready' && <input type="checkbox" checked={selected} onChange={() => toggleCandidate(email)} className="h-4 w-4 accent-indigo-600" aria-label={`Select ${candidate.name || email}`} />}
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">{String(candidate.name || email).charAt(0).toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{candidate.name || email}</p>
                      {candidate.source_type === 'imported' && (
                        <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" title="Imported for this job only — no resume">Imported</span>
                      )}
                      {candidate.missed_interview && (
                        <span
                          className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                          title={`Previous interview not attended${candidate.missed_scheduled_date ? ` on ${candidate.missed_scheduled_date}` : ''}${candidate.missed_scheduled_time ? ` at ${formatTime12hr(candidate.missed_scheduled_time)}` : ''}`}
                        >
                          Missed
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">{email}</p>
                  </div>
                  <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                    {candidate.scheduled_date ? <p>{candidate.scheduled_date}{candidate.scheduled_time ? ` · ${formatTime12hr(candidate.scheduled_time)}` : ''}</p> : <p className="capitalize">{candidateStage(candidate) || activeBucket}</p>}
                    {candidate.interviewer_name && <p className="mt-0.5">{candidate.interviewer_name}</p>}
                    {/* Completed → view the interview report. AI shows the scored result; manual shows the
                        note-taker meeting analysis (both resolved inside openResultPanel). */}
                    {activeBucket === 'completed' && (
                      <button type="button" onClick={() => openResultPanel(candidate)} className="mt-2 rounded-lg border border-indigo-200 px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-900/20">
                        {(candidate.calendar?.interview_type === 'ai' || candidate.interview_type === 'ai') ? 'View result' : 'View analysis'}
                      </button>
                    )}
                    {/* Booked MANUAL interview → Join Meet (mirrors the candidate Overview join button). */}
                    {activeBucket === 'booked' && candidate.calendar?.interview_type === 'manual' && candidate.calendar?.google_meet_link && (
                      <button type="button" onClick={() => handleJoinMeet(candidate)} disabled={joiningEmail === email}
                        className="mt-2 inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60">
                        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M15 10l4.5-2.5v9L15 14M4 7h9a1 1 0 011 1v8a1 1 0 01-1 1H4a1 1 0 01-1-1V8a1 1 0 011-1z" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        {joiningEmail === email ? 'Joining…' : 'Join Meet'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && !error && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2.5 dark:border-gray-700">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Page {safePage} of {totalPages} · {filteredCandidates.length} {search.trim() ? 'match(es)' : 'total'}
            </span>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">Prev</button>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">Next</button>
            </div>
          </div>
        )}
      </div>

      {renderResultPanel()}

      <RejectCandidateModal
        open={!!rejectTarget}
        candidate={rejectTarget}
        searchId={searchId}
        sessionId={sourceSessionId}
        onClose={() => setRejectTarget(null)}
        onRejected={() => {
          setRejectTarget(null);
          setResultPanel({ candidate: null, loading: false, recalculating: false, result: null, analysis: null, error: '' });
          loadWorkspace();
        }}
      />

      <InterviewScheduler
        isOpen={schedulerOpen}
        onClose={() => setSchedulerOpen(false)}
        searchId={searchId}
        sourceSessionId={sourceSessionId}
        companyId={companyId}
        jobTitle={jobTitle}
        candidates={selectedCandidates.length > 0 ? selectedCandidates : buckets.ready}
        enableInterviewTypeChoice
        initialInterviewSetup={selectedBlueprint || enabledInterviewSetup}
        featureAccess={featureAccess}
        onScheduleComplete={() => {
          setSchedulerOpen(false);
          setAssistantOpen(false);
          setSelectedEmails(new Set());
          loadWorkspace();
        }}
      />
      {createPortal(
        <Toast
          message={toastState.message}
          type={toastState.type}
          visible={toastState.visible}
          onClose={() => setToastState(prev => ({ ...prev, visible: false }))}
          duration={5000}
        />,
        document.body
      )}
    </div>
  );
};

export default InterviewWorkspace;
